import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { ArrowRight, Columns3, List, MessageSquare } from "lucide-react"

import { DataState } from "@/components/DataState"
import { PageHeader } from "@/components/PageHeader"
import { Toolbar, ToolbarField } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { BotonComentario, rutaDeLaReferencia } from "@/components/Comentarios"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuth } from "@/lib/auth"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import { fetchComentarios } from "@/routes/comentarios/api"
import type { AmbitoComentario, Comentario } from "@/routes/comentarios/api"

/**
 * LA BANDEJA: qué queda por mirar en esta sociedad.
 *
 * Es la cuarta forma de comentar que pidió Cesar —«un hilo general por sociedad
 * y período»— y a la vez la vista que hace útiles a las otras tres. Sin ella,
 * un comentario dejado sobre una celda del P&L de marzo sólo existe para quien
 * vuelva a abrir esa celda.
 *
 * ===========================================================================
 * AGRUPA POR DÓNDE SE RESUELVE, NO POR CUÁNDO SE ESCRIBIÓ
 * ===========================================================================
 *
 * Una lista cronológica obliga a saltar entre pantallas: una cifra, un asiento,
 * otra cifra, una factura. Agrupada por ámbito, quien entra a resolver
 * comentarios de cobranza los resuelve todos seguidos sin cambiar de contexto.
 *
 * ===========================================================================
 * NO DUPLICA EL HILO
 * ===========================================================================
 *
 * Cada fila abre el MISMO diálogo que el botón de la pantalla de origen
 * (`BotonComentario`). No hay una segunda forma de contestar: una bandeja con su
 * propio editor y su propio «resolver» sería un segundo sitio donde arreglar
 * cualquier cambio, y los dos divergirían.
 */

const AMBITOS: Array<{ id: AmbitoComentario | "todos"; label: string }> = [
  { id: "todos", label: "Todo" },
  { id: "cifra", label: "Cifras de reportes" },
  { id: "asiento", label: "Asientos" },
  { id: "documento", label: "Comprobantes" },
  { id: "factura", label: "Facturas por cobrar" },
  { id: "general", label: "Generales" },
]

const TITULO: Record<AmbitoComentario, string> = {
  cifra: "Sobre cifras de reportes",
  asiento: "Sobre asientos",
  documento: "Sobre comprobantes",
  factura: "Sobre facturas por cobrar",
  general: "Generales",
}

export function PendientesPage() {
  const { activeTenantId, activeEntityId } = useAuth()
  const [ambito, setAmbito] = React.useState<AmbitoComentario | "todos">("todos")
  const [estado, setEstado] = React.useState<"abierto" | "resuelto" | "todos">("abierto")
  /*
    LISTA O KANBAN. Cesar, punto 11 de la cuarta tanda: «en pendientes deberías
    de tenerlo a nivel lista y también a nivel kanban».

    No son dos formas de pintar lo mismo: contestan preguntas distintas. La
    LISTA agrupa por ÁMBITO —sobre qué se marcó: una cifra, un asiento, una
    factura— y sirve para repasar un área. El KANBAN agrupa por ESTADO y sirve
    para vaciar la cola: lo que importa ahí es qué queda por resolver y moverlo.
  */
  const [vista, setVista] = React.useState<"lista" | "kanban">("lista")

  /*
    EL KANBAN NECESITA LAS DOS COLUMNAS, así que pide `todos` pase lo que pase
    en el filtro. Con el filtro en «abierto» —que es el de salida— una de las
    dos columnas saldría vacía siempre, y una columna vacía por el filtro se lee
    como «no hay nada resuelto».
  */
  const estadoConsultado = vista === "kanban" ? "todos" : estado

  const q = useQuery({
    queryKey: ["comentarios", activeTenantId, activeEntityId, ambito, estadoConsultado],
    queryFn: () =>
      fetchComentarios({
        entityId: activeEntityId,
        ambito: ambito === "todos" ? undefined : ambito,
        estado: estadoConsultado,
      }),
    enabled: Boolean(activeTenantId) && Boolean(activeEntityId),
  })

  const porAmbito = React.useMemo(() => {
    const mapa = new Map<AmbitoComentario, Comentario[]>()
    for (const c of q.data?.comentarios ?? []) {
      const lista = mapa.get(c.ambito)
      if (lista) lista.push(c)
      else mapa.set(c.ambito, [c])
    }
    return [...mapa.entries()]
  }, [q.data])

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Pendientes"
        title={t("Lo que alguien marcó")}
        description={t("Las observaciones dejadas sobre cifras, asientos, comprobantes y facturas de esta sociedad. Una queda abierta hasta que alguien dice qué hizo con ella.")}
        actions={
          <BotonComentario
            variante="texto"
            ancla={{ ambito: "general", referencia: "", rotulo: "Nota general" }}
          />
        }
      />

      <Toolbar>
        <ToolbarField label={t("Sobre qué")} htmlFor="ambito-pend">
          <Select value={ambito} onValueChange={(v) => setAmbito(v as AmbitoComentario | "todos")}>
            <SelectTrigger id="ambito-pend" className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AMBITOS.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        {/*
          EL FILTRO DE ESTADO SE ESCONDE EN KANBAN, no se deja puesto sin hacer
          nada. El kanban es columnas POR estado: un filtro de estado encima
          dejaría una columna vacía y nadie sabría si fue el filtro o los datos.
        */}
        {vista === "lista" ? (
          <ToolbarField label={t("Estado")} htmlFor="estado-pend">
            <Select value={estado} onValueChange={(v) => setEstado(v as typeof estado)}>
              <SelectTrigger id="estado-pend" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {/* Abierto primero y por defecto: una bandeja de pendientes que
                    arranca enseñando lo ya resuelto obliga a filtrar para usarla. */}
                <SelectItem value="abierto">{t("Sin resolver")}</SelectItem>
                <SelectItem value="resuelto">{t("Resueltos")}</SelectItem>
                <SelectItem value="todos">{t("Todos")}</SelectItem>
              </SelectContent>
            </Select>
          </ToolbarField>
        ) : null}

        <div className="ml-auto flex items-center rounded-md border bg-surface p-0.5">
          {([["lista", "Lista"], ["kanban", "Kanban"]] as const).map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              onClick={() => setVista(id)}
              aria-pressed={vista === id}
              className={cn(
                "flex h-6 items-center gap-1 rounded-sm px-2 text-2xs font-medium transition-colors",
                vista === id
                  ? "bg-surface-sunken text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {id === "lista" ? <List className="h-3 w-3" /> : <Columns3 className="h-3 w-3" />}
              {t(rotulo)}
            </button>
          ))}
        </div>
      </Toolbar>

      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={Boolean(q.data) && (q.data?.comentarios.length ?? 0) === 0}
        emptyMessage={
          estado === "abierto"
            ? "No hay nada marcado sin resolver en esta sociedad."
            : "No hay comentarios que cumplan el filtro."
        }
        emptyHint={t("Se marca desde donde se ve: al lado de una cifra, de un asiento, de un comprobante o de una factura.")}
        onRetry={() => void q.refetch()}
        rows={5}
        columns={[100]}
      >
        {vista === "kanban" ? (
          <Kanban comentarios={q.data?.comentarios ?? []} />
        ) : (
        <div className="space-y-3">
          {porAmbito.map(([amb, lista]) => (
            <WorkPanel
              key={amb}
              title={TITULO[amb]}
              meta={<Badge variant="outline">{lista.length}</Badge>}
            >
              <ul className="divide-y">
                {lista.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-2.5 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
                        <span className="font-medium">{c.rotulo || c.referencia}</span>
                        {c.anio ? (
                          <span className="code text-2xs text-muted-foreground">
                            {c.anio}
                            {c.mes ? `-${String(c.mes).padStart(2, "0")}` : ""}
                          </span>
                        ) : null}
                        {c.estado === "resuelto" ? (
                          <Badge variant="outline" className="border-success/40 text-success">
                            {t("resuelto")}
                          </Badge>
                        ) : null}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-2xs leading-5 text-muted-foreground">
                        {c.texto}
                      </p>
                      <p className="mt-px text-2xs text-muted-foreground">
                        {c.autor ?? c.autor_email ?? "Alguien"} ·{" "}
                        {new Date(c.creado_en).toLocaleDateString()}
                      </p>
                    </div>
                    {/*
                      El MISMO botón que en la pantalla de origen: abre el mismo
                      hilo y tiene el mismo «resolver». Ver la cabecera.
                    */}
                    <span className="flex shrink-0 items-center gap-1.5">
                      {rutaDeLaReferencia(c.referencia) ? (
                        <Link to={rutaDeLaReferencia(c.referencia) as string}
                              className="text-2xs text-muted-foreground hover:text-foreground hover:underline">
                          {t("Ir a la vista")}
                        </Link>
                      ) : null}
                      <BotonComentario
                        variante="texto"
                        ancla={{
                          ambito: c.ambito,
                          referencia: c.referencia,
                          rotulo: c.rotulo || c.referencia,
                          anio: c.anio,
                          mes: c.mes,
                        }}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </WorkPanel>
          ))}
        </div>
        )}
      </DataState>

      <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
        <MessageSquare className="h-3 w-3" aria-hidden="true" />
        {t("¿Falta algo aquí? Se marca desde donde se ve.")}{" "}
        <Link to="/finanzas/pnl" className="text-brand underline-offset-2 hover:underline">
          {t("Ir al P&L")}
        </Link>
        <ArrowRight className="h-3 w-3" aria-hidden="true" />
      </p>
    </div>
  )
}


/**
 * EL KANBAN: dos columnas, por ESTADO.
 *
 * Cesar, punto 11 de la cuarta tanda. No es otra forma de pintar la lista: la
 * lista agrupa por ÁMBITO —sobre qué se marcó— y sirve para repasar un área;
 * esto agrupa por estado y sirve para VACIAR la cola.
 *
 * DOS COLUMNAS Y NO CINCO, porque dos son los estados que existen: `abierto` y
 * `resuelto`. Inventar «en curso» o «bloqueado» habría dado un tablero más
 * bonito y un estado que no se guarda en ninguna parte — y entonces mover una
 * tarjeta ahí no significaría nada y se perdería al recargar.
 *
 * NO SE ARRASTRA, SE RESUELVE DESDE LA TARJETA. Resolver exige una RESPUESTA
 * —es lo que convierte «lo miré» en «esto es lo que pasaba»— y un arrastre no
 * puede pedirla. El mismo botón de siempre abre el mismo hilo: dos caminos para
 * resolver acabarían guardando cosas distintas.
 */
function Kanban({ comentarios }: { comentarios: Comentario[] }) {
  const columnas = [
    { id: "abierto" as const, titulo: "Sin resolver" },
    { id: "resuelto" as const, titulo: "Resueltos" },
  ]

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {columnas.map((col) => {
        const items = comentarios.filter((c) => c.estado === col.id)
        return (
          <WorkPanel
            key={col.id}
            title={t(col.titulo)}
            meta={<Badge variant="outline">{items.length}</Badge>}
          >
            {items.length === 0 ? (
              <p className="px-2.5 py-2 text-2xs text-muted-foreground">
                {col.id === "abierto"
                  ? t("Nada pendiente. Es el estado al que se quiere llegar.")
                  : t("Todavía no se ha resuelto ninguno.")}
              </p>
            ) : (
              <ul className="space-y-1.5 p-2.5">
                {items.map((c) => (
                  <li
                    key={c.id}
                    className="space-y-1 rounded-md border bg-surface px-2.5 py-2"
                  >
                    <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
                      <span className="font-medium">{c.rotulo || c.referencia}</span>
                      {/*
                        EL ÁMBITO VA EN LA TARJETA, y en la lista no hace falta
                        porque allí es el título del grupo. Sin él, en el kanban
                        una tarjeta no dice si habla de una cifra o de una
                        factura, que es lo primero que hay que saber para
                        atenderla.
                      */}
                      <Badge variant="outline">{TITULO[c.ambito]}</Badge>
                      {c.anio ? (
                        <span className="code text-2xs text-muted-foreground">
                          {c.anio}
                          {c.mes ? `-${String(c.mes).padStart(2, "0")}` : ""}
                        </span>
                      ) : null}
                    </p>
                    <p className="line-clamp-3 text-2xs leading-5 text-muted-foreground">
                      {c.texto}
                    </p>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-2xs text-muted-foreground">
                        {c.autor ?? c.autor_email ?? "Alguien"} ·{" "}
                        {new Date(c.creado_en).toLocaleDateString()}
                      </span>
                      <BotonComentario
                        variante="texto"
                        ancla={{
                          ambito: c.ambito,
                          referencia: c.referencia,
                          rotulo: c.rotulo || c.referencia,
                          anio: c.anio,
                          mes: c.mes,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </WorkPanel>
        )
      })}
    </div>
  )
}
